import React, { useMemo, useEffect } from "react";
import EntityDialog from "../../components/EntityDialog";
import TextField from "@material-ui/core/TextField";
import moment from "moment";
import { License } from "../../models/model";
import { useForm } from "reactcoregk/hooks";
import Select from "../../components/Inputs/Select";
import {
  AUTHORITY,
  MULTI_TENANT_MODES,
  PROXY_MODES,
} from "../../constants/enums";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import { Auth } from "../../models/Auth";

const licence = new License();
const singleTenant = MULTI_TENANT_MODES[0].value;

const isPastDate = (value) => {
  if (!value) return false;
  return moment(value).isBefore(moment());
};

const isPastDate24h = (value) => {
  if (!value) return false;
  return moment(value).isBefore(moment().subtract(24, "hours"));
};

function CreateLicenseDialog(props) {
  const { clientId, editLicense } = props;
  const isLicenseExpired = Boolean(editLicense?.isExpired);
  const [form, handleChange, setForm] = useForm(editLicense || licence);
  const [arbisAiEnabled, setArbisAiEnabled] = React.useState(
    editLicense ? Boolean(editLicense.arbisAiEnabled) : false
  );
  const [arbisAiStartsAt, setArbisAiStartsAt] = React.useState(
    editLicense?.arbisAiStartsAt || ""
  );
  const [arbisAiExpiresAt, setArbisAiExpiresAt] = React.useState(
    editLicense?.arbisAiExpiresAt || ""
  );

  const isEdit = Boolean(editLicense?.id);
  const minDateTime = moment().format("YYYY-MM-DDTHH:mm");
  const minStartDateTime = moment().subtract(24, "hours").format("YYYY-MM-DDTHH:mm");

  // Only ADMIN and LICENSE_ADMIN can manage Arbis AI license
  const canManageArbisAi =
    Auth.hasRole(AUTHORITY.ADMIN) ||
    Auth.hasRole(AUTHORITY.LICENSE_ADMIN) ||
    Auth.hasRole(`ROLE_${AUTHORITY.ADMIN}`) ||
    Auth.hasRole(`ROLE_${AUTHORITY.LICENSE_ADMIN}`);

  // Track whether user has toggled the switch from its original state
  const initialArbisAiEnabled = Boolean(editLicense?.arbisAiEnabled);
  const hasToggleChanged = isEdit && arbisAiEnabled !== initialArbisAiEnabled;

  // In edit mode, dates are read-only only when AI is already enabled (existing evolvable license)
  // When newly enabling AI on an existing license, dates should be editable
  const arbisAiStartsAtReadonly = isEdit && initialArbisAiEnabled;
  const arbisAiExpiresAtReadonly = isEdit && initialArbisAiEnabled;

  useEffect(() => {
    if (editLicense) {
      setForm(editLicense);
      setArbisAiEnabled(Boolean(editLicense.arbisAiEnabled));
      setArbisAiStartsAt(editLicense.arbisAiStartsAt || "");
      setArbisAiExpiresAt(editLicense.arbisAiExpiresAt || "");
    } else {
      setForm(licence);
      setArbisAiEnabled(false);
      setArbisAiStartsAt("");
      setArbisAiExpiresAt("");
    }
  }, [editLicense, setForm]);

  // Pre-populate Arbis AI dates from license dates when toggling on
  useEffect(() => {
    if (arbisAiEnabled) {
      if (!arbisAiStartsAt && form.startsAt) {
        setArbisAiStartsAt(moment(form.startsAt).format("YYYY-MM-DDTHH:mm"));
      }
      if (!arbisAiExpiresAt && form.expiresAt) {
        setArbisAiExpiresAt(moment(form.expiresAt).format("YYYY-MM-DDTHH:mm"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arbisAiEnabled]);

  const finalForm = useMemo(() => {
    return {
      ...form,
      clientId: clientId,
      startsAt: moment(form.startsAt).format("YYYY-MM-DDTHH:mm:ss.SSS"),
      expiresAt: moment(form.expiresAt).format("YYYY-MM-DDTHH:mm:ss.SSS"),
      arbisAiEnabled,
      arbisAiStartsAt: arbisAiEnabled
        ? moment(arbisAiStartsAt).format("YYYY-MM-DDTHH:mm:ss.SSS")
        : null,
      arbisAiExpiresAt: arbisAiEnabled
        ? moment(arbisAiExpiresAt).format("YYYY-MM-DDTHH:mm:ss.SSS")
        : null,
    };
  }, [clientId, form, arbisAiEnabled, arbisAiStartsAt, arbisAiExpiresAt]);

  const startsAtPast = !isEdit && form.startsAt && isPastDate24h(form.startsAt);
  const expiresAtPast = !isEdit && form.expiresAt && isPastDate(form.expiresAt);

  // Arbis AI starts at validation — only check past when field is editable
  const arbisAiStartsAtPast =
    arbisAiEnabled &&
    arbisAiStartsAt &&
    !arbisAiStartsAtReadonly &&
    isPastDate24h(arbisAiStartsAt);

  const arbisAiExpiresAtPast =
    arbisAiEnabled &&
    arbisAiExpiresAt &&
    !arbisAiExpiresAtReadonly &&
    isPastDate(arbisAiExpiresAt);

  const licenseStartsAt = form.startsAt
    ? moment(form.startsAt).format("YYYY-MM-DDTHH:mm")
    : minDateTime;
  const licenseExpiresAt = form.expiresAt
    ? moment(form.expiresAt).format("YYYY-MM-DDTHH:mm")
    : "";

  // Arbis AI startsAt out of license range
  const arbisAiStartsAtOutOfRange =
    arbisAiEnabled &&
    arbisAiStartsAt &&
    !arbisAiStartsAtReadonly &&
    (moment(arbisAiStartsAt).isBefore(moment(form.startsAt)) ||
      (form.expiresAt &&
        moment(arbisAiStartsAt).isAfter(moment(form.expiresAt))));

  // Arbis AI expiresAt out of license range
  const arbisAiExpiresAtOutOfRange =
    arbisAiEnabled &&
    arbisAiExpiresAt &&
    !arbisAiExpiresAtReadonly &&
    (moment(arbisAiExpiresAt).isBefore(moment(form.startsAt)) ||
      (form.expiresAt &&
        moment(arbisAiExpiresAt).isAfter(moment(form.expiresAt))));

  // Arbis AI startsAt must be before expiresAt
  const arbisAiStartsAfterExpires =
    arbisAiEnabled &&
    arbisAiStartsAt &&
    arbisAiExpiresAt &&
    moment(arbisAiStartsAt).isAfter(moment(arbisAiExpiresAt));

  const hasDateError =
    startsAtPast ||
    expiresAtPast ||
    arbisAiStartsAtPast ||
    arbisAiExpiresAtPast ||
    arbisAiStartsAtOutOfRange ||
    arbisAiExpiresAtOutOfRange ||
    arbisAiStartsAfterExpires;

  const isValid = isEdit
    ? hasToggleChanged &&
      (!arbisAiEnabled || (arbisAiStartsAt && arbisAiExpiresAt)) &&
      !hasDateError
    : form.startsAt &&
      form.expiresAt &&
      form.numberOfTenants > 0 &&
      (!arbisAiEnabled || (arbisAiStartsAt && arbisAiExpiresAt)) &&
      !hasDateError;

  const handleChangeTenant = (e) => {
    const value = e.target.value;
    setForm((prevState) => ({
      ...form,
      multiTenantMode: value,
      numberOfTenants: value === singleTenant ? 1 : prevState.numberOfTenants,
    }));
  };

  // Compute arbis AI field min/max
  const arbisAiStartsAtMin =
    licenseStartsAt > minStartDateTime ? licenseStartsAt : minStartDateTime;
  const arbisAiExpiresAtMin =
    arbisAiStartsAt > minDateTime ? arbisAiStartsAt : minDateTime;

  // Helper to get error text for Arbis AI starts at
  const getArbisAiStartsAtError = () => {
    if (arbisAiStartsAtPast) return "Arbis AI start date cannot be more than 24 hours in the past";
    if (arbisAiStartsAtOutOfRange)
      return "Arbis AI start date must be within the license date range";
    if (arbisAiStartsAfterExpires)
      return "Arbis AI start date must be before the expiry date";
    return "";
  };

  // Helper to get error text for Arbis AI expires at
  const getArbisAiExpiresAtError = () => {
    if (arbisAiExpiresAtPast)
      return "Arbis AI expiry date cannot be in the past";
    if (arbisAiExpiresAtOutOfRange)
      return "Arbis AI expiry date must be within the license date range";
    return "";
  };

  return (
    <EntityDialog
      titleOverride={isEdit ? "Edit License" : "Add license"}
      {...props}
      entity={licence}
      form={finalForm}
      isValid={Boolean(isValid)}
      maxWidth={"sm"}
    >
      <form>
        {!isEdit && (
          <>
            <Select
              variant="standard"
              fullWidth
              margin={"normal"}
              value={form.proxyMode}
              onChange={handleChange("proxyMode")}
              label="Proxy Mode"
              controlId={"proxyMode"}
              options={PROXY_MODES}
            />
            <Select
              variant="standard"
              fullWidth
              margin={"normal"}
              value={form.multiTenantMode}
              onChange={handleChangeTenant}
              label="Tenant Mode"
              controlId={"multiTenantMode"}
              options={MULTI_TENANT_MODES}
            />
            {form.multiTenantMode !== singleTenant && (
              <TextField
                variant="standard"
                fullWidth
                margin={"normal"}
                disabled={form.multiTenantMode === singleTenant}
                type="number"
                InputProps={{ inputProps: { min: 1 } }}
                value={form.numberOfTenants}
                onChange={handleChange("numberOfTenants")}
                label="Number of Tenants"
              />
            )}
            <TextField
              variant="standard"
              fullWidth
              margin={"normal"}
              type="datetime-local"
              value={form.startsAt}
              onChange={handleChange("startsAt")}
              label="Starts at"
              inputProps={{ min: minStartDateTime }}
              InputLabelProps={{
                shrink: true,
              }}
              error={Boolean(startsAtPast)}
              helperText={
                startsAtPast ? "Start date cannot be more than 24 hours in the past" : ""
              }
            />
            <TextField
              variant="standard"
              margin={"normal"}
              fullWidth
              type="datetime-local"
              value={form.expiresAt}
              onChange={handleChange("expiresAt")}
              label="Expires at"
              inputProps={{ min: minDateTime }}
              InputLabelProps={{
                shrink: true,
              }}
              error={Boolean(expiresAtPast)}
              helperText={
                expiresAtPast ? "Expiry date cannot be in the past" : ""
              }
            />
          </>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={arbisAiEnabled}
              onChange={(e) => setArbisAiEnabled(e.target.checked)}
              color="primary"
              disabled={isLicenseExpired || !canManageArbisAi}
            />
          }
          label={
            !canManageArbisAi
              ? "Enable Arbis AI License (Insufficient permissions)"
              : isLicenseExpired
              ? "Enable Arbis AI License (License expired)"
              : "Enable Arbis AI License"
          }
          style={{ marginTop: 16 }}
        />
        {arbisAiEnabled && (
          <>
            <TextField
              variant="standard"
              margin={"normal"}
              fullWidth
              type="datetime-local"
              value={arbisAiStartsAt}
              onChange={(e) => setArbisAiStartsAt(e.target.value)}
              label="Arbis AI Starts at"
              disabled={arbisAiStartsAtReadonly || !canManageArbisAi}
              inputProps={{
                min: arbisAiStartsAtMin,
                ...(licenseExpiresAt ? { max: licenseExpiresAt } : {}),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              error={Boolean(
                arbisAiStartsAtPast ||
                  arbisAiStartsAtOutOfRange ||
                  arbisAiStartsAfterExpires
              )}
              helperText={
                arbisAiStartsAtReadonly
                  ? "Start date is in the past and cannot be modified"
                  : getArbisAiStartsAtError()
              }
            />
            <TextField
              variant="standard"
              margin={"normal"}
              fullWidth
              type="datetime-local"
              value={arbisAiExpiresAt}
              onChange={(e) => setArbisAiExpiresAt(e.target.value)}
              label="Arbis AI Expires at"
              disabled={arbisAiExpiresAtReadonly || !canManageArbisAi}
              inputProps={{
                min:
                  arbisAiExpiresAtMin > licenseStartsAt
                    ? arbisAiExpiresAtMin
                    : licenseStartsAt,
                ...(licenseExpiresAt ? { max: licenseExpiresAt } : {}),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              error={Boolean(
                arbisAiExpiresAtPast || arbisAiExpiresAtOutOfRange
              )}
              helperText={getArbisAiExpiresAtError()}
            />
          </>
        )}
      </form>
    </EntityDialog>
  );
}

export default CreateLicenseDialog;
